// src/pos-app/surfaces/cashier/OrdersPanelNative.js
//
// F12a-N Task 6 — the native pending-orders panel (design contract:
// docs/mockups/f12a-n/cashier-sell.html, "ORDERS PANEL": `.opanel` list ->
// `.od-` detail, a single panel that swaps between a grouped card list and
// an order-detail master/detail pair with a Back link — never two separate
// popups). Rendered from SellSurface (which owns pendingOrders /
// printedOrders / the 10s occupation poll, same instances TableSelect and
// the bill-request banners already read).
//
// `embedded` (Task N — user-QC finding 3, additive, default false): renders
// the SAME list<->detail component as a full-pane fill of its parent
// (position:static, no scrim, no dismiss "X" — there is nothing to "close",
// it's a persistent tab) instead of the original fixed slide-in overlay
// (scrim + `position:fixed` panel). SellSurface now mounts this ALWAYS
// `embedded`, driven by the bottom Orders tab (`isOpen` = CashierStation's
// `ordersTabActive`) — the top-bar Orders button that used to open the
// non-embedded slide-in is gone (see TopBarExtras.js), closing a real
// duplication bug: the badge showed a pending count while the Orders TAB
// still rendered a separate, stale "No orders yet" empty state. The
// non-embedded slide-in mode is left in place (unused by this branch) since
// it is a genuine, documented reuse candidate for a future overlay-style
// call site — not because anything here still calls it that way.
//
// ── Reuse-vs-rebuild call (documented per the task brief) ──────────────────
// REUSED, zero edits: DeletionConfirmModal (components/common), via a LOCAL
//   useDeletionConfirm() instance — the reason-capture UI is this panel's own
//   concern, exactly like legacy TableOrderModal/CashierSalesPage.js:1668-1732
//   owns its own useDeletionConfirm() call.
// NOT reused as components — components/pos/PendingOrderCard.js and
//   components/pos/modals/TableOrderModal.js: both are Tailwind-styled,
//   `Modal`-wrapped POPUPS (fixed-inset overlay + backdrop), which is a
//   fundamentally different interaction model from the mockup's single
//   sliding panel with in-panel Back navigation between list and detail.
//   Wrapping TableOrderModal inside this panel would nest a modal-in-a-panel
//   and pull in raw gray-50/purple-200 Tailwind classes that violate the
//   token/CSS-var contract every other native file in this surface follows
//   (CatalogBrowse/CartPanel/TableSelect were rebuilt from scratch for the
//   exact same reason in Tasks 2-4). Their BUSINESS LOGIC is faithfully
//   reproduced instead: the occupation fetch + markServed/requestBill inline
//   below (TableOrderModal.js:41-77), and loadOrderForEdit/printOrderPreview
//   extracted into lib/pendingOrderFlow.js (see that file's header for exact
//   source ranges). ChangeTableModal and EditingProtectionModal ARE reused
//   wholesale (plan's explicit call-out) but are rendered by SellSurface, not
//   here — they mutate/guard SHARED builder/pendingOrders state that
//   SellSurface already owns (same "background components" pattern Task 5
//   established for PaymentConfirmModal/SplitBillManager).
//
// Occupation data reuses SellSurface's already-polled `occupations` (10s
// cadence, the same list TableSelect colors its tiles from and the
// bill-request banners diff) rather than a fresh per-order-open fetch like
// legacy TableOrderModal's dedicated effect — a deliberate, documented
// efficiency deviation: markServed/requestBill/clearTable are best-effort,
// low-frequency actions, and reusing the shared poll avoids a redundant
// network round-trip on every panel open without weakening correctness
// (worst case is a 10s-stale occupation id, and the actions themselves still
// call the real occupationService with that id).
import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage, getEntityTranslation, translateDynamic } from '../../../context/LanguageContext';
import { occupationService } from '../../../services/api';
import useDeletionConfirm from '../../../hooks/useDeletionConfirm';
import DeletionConfirmModal from '../../../components/common/DeletionConfirmModal';
import {
    printOrderPreview,
    deletePendingOrderAction,
    deletePaidOrderAction,
    clearPaidOrder,
} from './lib/pendingOrderFlow';
import ManagerGate from '../../../components/pos/ManagerGate';

const CSS = `
.opn-scrim{position:fixed;inset:0;z-index:44;background:rgba(9,13,22,.45);
  -webkit-backdrop-filter:blur(2px);backdrop-filter:blur(2px);}
.opn-panel{position:fixed;z-index:45;inset-block:0;inset-inline-end:0;width:min(410px,92vw);
  background:color-mix(in srgb, var(--c-surface) 88%, transparent);border-inline-start:1px solid var(--c-border);
  -webkit-backdrop-filter:blur(24px) saturate(1.5);backdrop-filter:blur(24px) saturate(1.5);
  box-shadow:0 -12px 40px rgba(2,8,23,.25);display:flex;flex-direction:column;}
/* embedded (Task N finding 3) — a full-pane fill of the Orders tab instead
   of a fixed slide-in: no floating position, no backdrop/shadow, no border
   (the tab pane already provides the glass surface). */
.opn-panel.opn-embedded{position:static;inset:auto;z-index:auto;width:100%;flex:1;min-height:0;
  border-inline-start:none;background:transparent;box-shadow:none;
  -webkit-backdrop-filter:none;backdrop-filter:none;}

.opn-head{flex:none;display:flex;align-items:center;gap:10px;padding:16px 18px 12px;
  border-bottom:1px solid color-mix(in srgb, var(--c-border) 55%, transparent);}
.opn-title{flex:1;min-width:0;font-size:17px;font-weight:800;letter-spacing:-.2px;color:var(--c-ink);
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.opn-count{flex:none;font-size:11px;font-weight:800;color:var(--c-green);background:var(--c-greenLight);
  border:1px solid var(--c-greenBorder);border-radius:999px;padding:3px 10px;font-variant-numeric:tabular-nums;}
.opn-x{position:relative;flex:none;width:32px;height:32px;border-radius:9px;border:1px solid var(--c-border);
  cursor:pointer;display:flex;align-items:center;justify-content:center;color:var(--c-muted);background:var(--c-faint);}
.opn-x:hover{color:var(--c-red);background:var(--c-redLight);border-color:var(--c-redBorder);}
.opn-back{display:inline-flex;align-items:center;gap:6px;font-size:12.5px;font-weight:700;color:var(--c-muted);
  padding:6px 8px;margin-inline-start:-8px;border-radius:8px;border:none;background:transparent;cursor:pointer;font-family:inherit;}
.opn-back:hover{color:var(--c-green);background:var(--c-greenLight);}

.opn-body{flex:1;min-height:0;overflow-y:auto;padding:12px 16px;}

.opn-empty{height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;
  color:var(--c-muted);text-align:center;padding:24px;}
.opn-empty-ic{width:60px;height:60px;border-radius:18px;background:var(--c-faint);border:1px solid var(--c-border);
  display:flex;align-items:center;justify-content:center;color:var(--c-muted);}
.opn-empty-t1{font-size:14px;font-weight:700;color:var(--c-slate);}
.opn-empty-t2{font-size:12px;max-width:220px;font-weight:600;}

.opn-group{font-size:10.5px;font-weight:800;text-transform:uppercase;letter-spacing:1px;color:var(--c-muted);
  margin:10px 2px 7px;}
.opn-card{position:relative;width:100%;text-align:start;display:flex;align-items:center;gap:12px;
  padding:12px 14px;margin-bottom:8px;background:var(--c-surface);border:1px solid var(--c-border);
  border-inline-start:4px solid var(--oc,var(--c-green));border-radius:13px;box-shadow:0 1px 3px rgba(15,23,42,.06);
  cursor:pointer;font-family:inherit;transition:box-shadow .12s,border-color .12s;}
.opn-card:hover{box-shadow:0 6px 16px rgba(15,23,42,.12);}
.opn-card.editing{outline:2px solid var(--c-amber);outline-offset:-1px;}
.opn-card-main{flex:1;min-width:0;}
.opn-card-t{font-size:13.5px;font-weight:800;color:var(--c-ink);}
.opn-card-d{display:block;font-size:11.5px;color:var(--c-muted);font-weight:600;margin-top:2px;}
.opn-sent{display:inline-block;margin-top:4px;font-size:9.5px;font-weight:800;letter-spacing:.8px;
  color:var(--c-green);background:var(--c-greenLight);border:1px solid var(--c-greenBorder);
  border-radius:6px;padding:2px 7px;}
.opn-card-side{flex:none;text-align:end;}
.opn-card-amt{font-size:14px;font-weight:800;color:var(--c-ink);font-variant-numeric:tabular-nums;}
.opn-card-time{display:block;font-size:11px;color:var(--c-muted);font-weight:600;margin-top:2px;
  font-variant-numeric:tabular-nums;}

.od-meta{display:flex;gap:8px;margin-bottom:12px;flex-wrap:wrap;}
.od-chip{font-size:11px;font-weight:700;color:var(--c-slate);background:var(--c-faint);border:1px solid var(--c-border);
  border-radius:999px;padding:5px 11px;}
.od-chip.bill{color:var(--c-amber);background:var(--c-amberLight);border-color:var(--c-amberBorder);}
.od-chip.served{color:var(--c-green);background:var(--c-greenLight);border-color:var(--c-greenBorder);}
.od-chip.paid{color:var(--c-green);background:var(--c-greenLight);border-color:var(--c-greenBorder);font-weight:800;}

.odetail-items{border:1px solid var(--c-border);border-radius:13px;background:var(--c-surface);padding:4px 14px;margin-bottom:12px;}
.oditem{display:flex;justify-content:space-between;gap:10px;padding:9px 0;
  border-bottom:1px solid color-mix(in srgb, var(--c-border) 65%, transparent);font-size:13px;}
.oditem:last-child{border-bottom:none;}
.oditem .q{color:var(--c-muted);font-weight:700;margin-inline-end:7px;font-variant-numeric:tabular-nums;}
.oditem .n{font-weight:600;flex:1;}
.oditem .p{font-weight:700;font-variant-numeric:tabular-nums;}
.oditem.total{font-weight:800;}

.od-actions{display:grid;grid-template-columns:1fr 1fr;gap:8px;}
.od-btn{position:relative;height:44px;border-radius:11px;font-size:12.5px;font-weight:700;
  background:var(--c-surface);border:1px solid var(--c-border);color:var(--c-slate);
  display:flex;align-items:center;justify-content:center;gap:7px;cursor:pointer;font-family:inherit;}
.od-btn svg{color:var(--c-muted);}
.od-btn:hover{border-color:var(--c-greenBorder);color:var(--c-green);}
.od-btn:hover svg{color:var(--c-green);}
.od-btn.danger:hover{border-color:var(--c-redBorder);color:var(--c-red);}
.od-btn.danger:hover svg{color:var(--c-red);}
.od-btn.primary{grid-column:1/-1;background:var(--c-green);border-color:var(--c-green);color:#fff;
  font-size:14px;font-weight:800;height:50px;box-shadow:0 6px 16px color-mix(in srgb, var(--c-green) 35%, transparent);}
.od-btn.primary svg{color:#fff;}
.od-btn.primary:hover{background:var(--c-greenDark);color:#fff;}
.od-btn:disabled{opacity:.5;cursor:not-allowed;}

.opn-pay{position:relative;z-index:48;margin-top:10px;border:1px solid var(--c-border);border-radius:13px;
  background:var(--c-surface);padding:10px;}
.opn-pay-t{font-size:12px;font-weight:700;color:var(--c-muted);margin-bottom:8px;}
.opn-pay-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;}
.opn-pay-btn{height:44px;border-radius:10px;border:1.5px solid var(--c-border);background:var(--c-faint);
  color:var(--c-slate);font-family:inherit;font-size:12.5px;font-weight:700;cursor:pointer;}
.opn-pay-btn:hover{border-color:var(--c-green);color:var(--c-green);background:var(--c-greenLight);}
`;

const GROUP_PALETTE = ['var(--c-green)', 'var(--c-amber)', 'var(--c-blue)', 'var(--c-red)', 'var(--c-mutedLight)'];

const CloseIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
);
const BackIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
);
const CheckIcon = (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
);
const BillIcon = (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 2v20l2-1.5L8 22l2-1.5L12 22l2-1.5L16 22l2-1.5L20 22V2l-2 1.5L16 2l-2 1.5L12 2l-2 1.5L8 2 6 3.5 4 2Z" /><path d="M8 12h8" />
    </svg>
);
const EditIcon = (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M17 3a2.8 2.8 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3Z" />
    </svg>
);
const PrintIcon = (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6 9V3h12v6" /><rect x="4" y="9" width="16" height="8" rx="2" /><path d="M6 15v6h12v-6" />
    </svg>
);
const TableIcon = (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 3h18v6H3zM3 15h18M9 9v12M15 9v12" />
    </svg>
);
const TrashIcon = (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0-1 14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2L4 6" />
    </svg>
);
const EmptyOrdersIcon = (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 2v20l2-1.5L8 22l2-1.5L12 22l2-1.5L16 22l2-1.5L20 22V2l-2 1.5L16 2l-2 1.5L12 2l-2 1.5L8 2 6 3.5 4 2Z" />
    </svg>
);

const fmtTime = (order) => order?.time ? order.time.slice(0, 5) : '';
const money = (currency, value) => `${currency}${(parseFloat(value) || 0).toFixed(2)}`;

export default function OrdersPanelNative({
    isOpen,
    embedded = false,
    onClose,
    pendingOrders,
    setPendingOrders,
    printedOrders,
    setPrintedOrders,
    pendingOrderIds,
    setPendingOrderIds,
    diningOptions,
    diningOptionObjects,
    occupations,
    currency,
    printableCategories,
    paymentMethods,
    tenantConfig,
    brandName,
    cashierName,
    editingOrderKey,
    initialOrderKey = null,
    onEditOrder,
    onChangeTable,
    onSettle,
    setSuccess,
    setError,
}) {
    const { t, language, translationDetails } = useLanguage();
    const [detailKey, setDetailKey] = useState(null);
    // Manager approval before the delete-reason modal — see handleDelete.
    const [deleteGateOpen, setDeleteGateOpen] = useState(false);
    const [payPickerOpen, setPayPickerOpen] = useState(false);
    const deletionConfirm = useDeletionConfirm();

    // Opening the panel directly onto an order (existing-table tap in
    // TableSelect, or a bill-notification's "View order") jumps straight to
    // its detail view; opening with no target lands on the list.
    useEffect(() => {
        if (isOpen) setDetailKey(initialOrderKey || null);
        if (!isOpen) setPayPickerOpen(false);
    }, [isOpen, initialOrderKey]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            // Task 8 item 9 — deletion-confirm sits ON TOP of the detail
            // view (its own REUSED, zero-edit DeletionConfirmModal already
            // self-dismisses via components/common/Modal.js's own Escape
            // effect). Without this guard, the SAME keypress ALSO fell
            // through to detailKey/list unwind underneath it — a real bug:
            // one Escape press would close the delete-reason modal AND step
            // the detail view back at once. Bail here and let Modal.js
            // exclusively own this keypress.
            if (deletionConfirm.isModalOpen) return;
            // Same reasoning for the manager gate, which also sits ON TOP of
            // the detail view and owns its own dismissal.
            if (deleteGateOpen) return;
            if (payPickerOpen) { setPayPickerOpen(false); return; }
            if (detailKey) { setDetailKey(null); return; }
            // embedded (Task N finding 3): there is nothing to "close" at
            // list level — it's a persistent tab, not a dismissible overlay
            // — so a bare Escape on the list does nothing further. The
            // non-embedded (unused-by-Sell) slide-in keeps its original
            // close-on-Escape behavior.
            if (!embedded) onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen, embedded, detailKey, payPickerOpen, deletionConfirm.isModalOpen, deleteGateOpen, onClose]);

    // Group order keys by dining option (mockup .ogroup), stable colour per
    // group keyed off `diningOptions`' order so the same option always gets
    // the same border colour across renders.
    const groups = useMemo(() => {
        const byOption = {};
        Object.entries(pendingOrders || {}).forEach(([key, order]) => {
            const label = order.diningOption || t('order');
            if (!byOption[label]) byOption[label] = [];
            byOption[label].push({ key, order });
        });
        const orderedLabels = (diningOptions || []).filter((d) => byOption[d]);
        Object.keys(byOption).forEach((label) => { if (!orderedLabels.includes(label)) orderedLabels.push(label); });
        return orderedLabels.map((label, idx) => ({
            label: getEntityTranslation(diningOptionObjects?.[label], language, translationDetails) || translateDynamic(label, t),
            color: GROUP_PALETTE[idx % GROUP_PALETTE.length],
            items: byOption[label].sort((a, b) => (a.order.time || '').localeCompare(b.order.time || '')),
        }));
    }, [pendingOrders, diningOptions, diningOptionObjects, language, translationDetails, t]);

    const totalCount = Object.keys(pendingOrders || {}).length;

    const order = detailKey ? pendingOrders?.[detailKey] : null;

    // Occupation for the detail order — reused from SellSurface's already-
    // polled `occupations` (see header comment).
    const occupation = useMemo(() => {
        if (!order) return null;
        const doId = diningOptionObjects?.[order.diningOption]?.id;
        if (doId == null) return null;
        return (occupations || []).find(
            (o) => String(o.dining_option_id) === String(doId) && String(o.table_number) === String(order.tableNumber)
        ) || null;
    }, [order, diningOptionObjects, occupations]);

    if (!isOpen) return null;

    const openDetail = (key) => { setDetailKey(key); setPayPickerOpen(false); };
    const backToList = () => { setDetailKey(null); setPayPickerOpen(false); };

    // Mark Served / Request Bill — faithful to TableOrderModal.js:57-77
    // (handleMarkServed / handleRequestBill), occupation resolved above
    // instead of a dedicated per-open fetch.
    const handleMarkServed = async () => {
        if (!occupation) return;
        try {
            await occupationService.markServed(occupation.id);
            setSuccess(t('table_marked_served', {}, 'Marked as served'));
        } catch (e) {
            setError(e.response?.data?.detail || t('failed_to_mark_served'));
        }
    };
    const handleRequestBill = async () => {
        if (!occupation) return;
        try {
            await occupationService.requestBill(occupation.id);
            setSuccess(t('bill_requested', {}, 'Bill requested'));
        } catch (e) {
            setError(e.response?.data?.detail || t('failed_to_request_bill'));
        }
    };

    const handlePrint = async () => {
        await printOrderPreview(detailKey, order, {
            pendingOrderIds, setPrintedOrders,
            currency, tenantConfig, brandName, t, printableCategories, occupation,
            setSuccess, setError,
        });
    };

    const handleEdit = () => {
        onEditOrder(detailKey, order);
        onClose();
    };

    const handleChangeTable = () => {
        onChangeTable(detailKey);
        onClose();
    };

    // Hand-over: the cashier gives the food to the customer. This is the
    // completion signal for a PAID order — deliberately a button, because
    // emitOrderCompletedEvent has no live listener and a receipts-only tenant
    // has no kitchen signal at all (spec §4.3). Ungated: handing food over
    // cannot lose money.
    const handleClear = async () => {
        await clearPaidOrder(detailKey, {
            setPendingOrders, setPrintedOrders, setPendingOrderIds,
            activeOccupation: occupation,
        });
        setDetailKey(null);
        if (setSuccess) setSuccess(t('order_cleared', {}, 'Order handed over'));
    };

    // Deleting a pending order needs a manager (client request 2026-08-26).
    // Gate FIRST, reason second — asking a cashier to justify a deletion that
    // is then refused is the wrong order. openDeleteReasonModal below is the
    // original handleDelete body, unchanged.
    const handleDelete = () => setDeleteGateOpen(true);

    // `approvedBy` is captured from the gate so the void names the MANAGER
    // who authorised it rather than the cashier who asked — that attribution
    // is the entire point of gating this action.
    const openDeleteReasonModal = (approvedBy) => {
        deletionConfirm.confirmDeletion({
            onDelete: async (reason) => {
                if (order.paid) {
                    // Already settled: void the transaction so the day's
                    // takings drop by exactly this order's value. Throws if
                    // the void fails, leaving the order in place rather than
                    // losing track of the money.
                    await deletePaidOrderAction(detailKey, reason, {
                        order, approvedBy, cashierName,
                        setPendingOrders, setPrintedOrders, setPendingOrderIds,
                        activeOccupation: occupation,
                    });
                } else {
                    await deletePendingOrderAction(detailKey, reason, {
                        order, cashierName, setPendingOrders, setPrintedOrders,
                        setPendingOrderIds, setSuccess, setError, setLoading: () => {}, t,
                    });
                }
                setDetailKey(null);
            },
            title: t('delete_order'),
            message: t('confirm_delete_order_for', { diningOption: order.diningOption, tableNumber: order.tableNumber }),
            itemName: `${order.diningOption} ${order.tableNumber} - ${money(currency, order.total)}`,
            requireReason: true,
        });
    };

    const handleSettle = (method) => {
        setPayPickerOpen(false);
        onSettle(detailKey, method);
        onClose();
    };

    return (
        <>
            {/* embedded (Task N finding 3): no scrim — it's a tab pane, not
                a dismissible overlay. */}
            {!embedded && <div className="opn-scrim" data-testid="orders-scrim" onClick={onClose} />}
            <div
                className={`opn-panel${embedded ? ' opn-embedded' : ''}`}
                role={embedded ? undefined : 'dialog'}
                aria-modal={embedded ? undefined : true}
                aria-label={t('pending_orders', {}, 'Pending orders')}
            >
                <style>{CSS}</style>

                {!detailKey ? (
                    <>
                        <div className="opn-head">
                            <span className="opn-title">{t('pending_orders', {}, 'Pending orders')}</span>
                            {totalCount > 0 && (
                                <span className="opn-count">{totalCount} {t('active', {}, 'active')}</span>
                            )}
                            {/* embedded: no dismiss "X" — nothing to close, the
                                Orders BottomTab is what switches away. */}
                            {!embedded && (
                                <button type="button" className="opn-x" aria-label={t('close')} onClick={onClose}>{CloseIcon}</button>
                            )}
                        </div>
                        <div className="opn-body">
                            {totalCount === 0 ? (
                                <div className="opn-empty">
                                    <div className="opn-empty-ic">{EmptyOrdersIcon}</div>
                                    <div className="opn-empty-t1">{t('no_pending_orders', {}, 'No pending orders')}</div>
                                    <div className="opn-empty-t2">{t('held_orders_appear_here', {}, 'Orders you hold will appear here')}</div>
                                </div>
                            ) : (
                                groups.map((group) => (
                                    <React.Fragment key={group.label}>
                                        <div className="opn-group">{group.label}</div>
                                        {group.items.map(({ key, order: o }) => (
                                            <button
                                                key={key}
                                                type="button"
                                                className={`opn-card${editingOrderKey === key ? ' editing' : ''}`}
                                                style={{ '--oc': group.color }}
                                                data-testid={`order-card-${key}`}
                                                onClick={() => openDetail(key)}
                                            >
                                                <span className="opn-card-main">
                                                    <span className="opn-card-t">{group.label} · {o.tableNumber}</span>
                                                    <span className="opn-card-d">{(o.products || []).length} {t('items')} · {o.cashier || t('unknown')}</span>
                                                    {printedOrders?.[key] && <span className="opn-sent">{t('sent', {}, 'SENT')}</span>}
                                                </span>
                                                <span className="opn-card-side">
                                                    <span className="opn-card-amt money">{money(currency, o.total)}</span>
                                                    <span className="opn-card-time">{fmtTime(o)}</span>
                                                </span>
                                            </button>
                                        ))}
                                    </React.Fragment>
                                ))
                            )}
                        </div>
                    </>
                ) : order ? (
                    <>
                        <div className="opn-head">
                            <button type="button" className="opn-back" onClick={backToList}>{BackIcon}{t('pending_orders', {}, 'Orders')}</button>
                            <span className="opn-title">{order.diningOption} · {order.tableNumber}</span>
                            {!embedded && (
                                <button type="button" className="opn-x" aria-label={t('close')} onClick={onClose}>{CloseIcon}</button>
                            )}
                        </div>
                        <div className="opn-body">
                            <div className="od-meta">
                                <span className="od-chip">{t('placed_at', { time: fmtTime(order) }, `Placed ${fmtTime(order)}`)}</span>
                                <span className="od-chip">{order.cashier || t('unknown')}</span>
                                {/* PAID is the load-bearing signal on this
                                    screen: it tells the cashier the money is
                                    in and all that remains is hand-over. */}
                                {order.paid && <span className="od-chip paid" data-testid="od-paid-chip">{t('paid', {}, 'Paid')}</span>}
                                {occupation?.served_at && <span className="od-chip served">{t('served', {}, 'Served')}</span>}
                                {(printedOrders?.[detailKey] || occupation?.bill_requested_at) && (
                                    <span className="od-chip bill">{t('bill_requested', {}, 'Bill requested')}</span>
                                )}
                                {editingOrderKey === detailKey && (
                                    <span className="od-chip bill">{t('editing_order')}</span>
                                )}
                            </div>

                            <div className="odetail-items">
                                {(order.products || []).map((p, idx) => (
                                    <div className="oditem" key={p.uniqueId || `${p.product_code}-${idx}`}>
                                        <span><span className="q money">{p.quantity}×</span><span className="n">{p.name}</span></span>
                                        <span className="p money">{money(currency, (parseFloat(p.selling_price) || 0) * (parseInt(p.quantity) || 1))}</span>
                                    </div>
                                ))}
                                <div className="oditem total">
                                    <span className="n">{t('total_incl_service_vat', {}, 'Total (incl. service & VAT)')}</span>
                                    <span className="p money">{money(currency, order.total)}</span>
                                </div>
                            </div>

                            <div className="od-actions">
                                <button type="button" className="od-btn" disabled={!occupation || !!occupation.served_at} onClick={handleMarkServed}>
                                    {CheckIcon}{t('mark_served', {}, 'Mark Served')}
                                </button>
                                <button type="button" className="od-btn" disabled={!occupation} onClick={handleRequestBill}>
                                    {BillIcon}{t('request_bill', {}, 'Request Bill')}
                                </button>
                                {/* A PAID order's items are already paid for —
                                    editing them would desynchronise the cart
                                    from the settled transaction, so Edit is
                                    not offered. */}
                                {!order.paid && (
                                    <button type="button" className="od-btn" onClick={handleEdit}>
                                        {EditIcon}{t('edit')}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="od-btn"
                                    data-testid="od-print"
                                    onClick={handlePrint}
                                >
                                    {PrintIcon}{t('print')}
                                </button>
                                <button type="button" className="od-btn" onClick={handleChangeTable}>
                                    {TableIcon}{t('table', {}, 'Table')}
                                </button>
                                <button type="button" className="od-btn danger" onClick={handleDelete}>
                                    {TrashIcon}{t('delete')}
                                </button>
                                {/* Charge vs Clear — the guard that stops a
                                    customer paying twice. A paid order has
                                    already been settled; offering Charge here
                                    would take the money again. */}
                                {order.paid ? (
                                    <button
                                        type="button"
                                        className="od-btn primary"
                                        data-testid="od-clear"
                                        onClick={handleClear}
                                    >
                                        {CheckIcon}{t('clear_order', {}, 'Clear')}
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="od-btn primary"
                                        data-testid="od-charge"
                                        onClick={() => setPayPickerOpen((v) => !v)}
                                    >
                                        {t('charge')}&nbsp;<span className="money">{money(currency, order.total)}</span>
                                    </button>
                                )}
                            </div>

                            {payPickerOpen && (
                                <div className="opn-pay" data-testid="pay-picker">
                                    <div className="opn-pay-t">{t('select_payment_method_complete', {}, 'Select a payment method to complete')}</div>
                                    <div className="opn-pay-grid">
                                        {(paymentMethods || []).map((method) => (
                                            <button
                                                key={method}
                                                type="button"
                                                className="opn-pay-btn"
                                                onClick={() => handleSettle(method)}
                                            >
                                                {translateDynamic(method, t)}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                ) : null}
            </div>

            <DeletionConfirmModal
                isOpen={deletionConfirm.isModalOpen}
                onClose={deletionConfirm.handleCancel}
                onConfirm={deletionConfirm.handleConfirm}
                loading={deletionConfirm.loading}
                {...deletionConfirm.modalProps}
            />

            {/* Manager approval for deleting a pending order. */}
            <ManagerGate
                open={deleteGateOpen}
                action="delete_order"
                context={{
                    orderKey: detailKey,
                    diningOption: order?.diningOption || null,
                    tableNumber: order?.tableNumber || null,
                    total: order?.total ?? null,
                }}
                onApprove={(approval) => {
                    setDeleteGateOpen(false);
                    openDeleteReasonModal(approval?.approved_by_name || null);
                }}
                onCancel={() => setDeleteGateOpen(false)}
            />
        </>
    );
}
